import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
    Avatar,
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Textarea,
    IconButton,
    Icon,
    Spinner,
    Button,
    Tooltip,
} from '@chakra-ui/react';
import { TbArrowUp, TbRefresh } from 'react-icons/tb';
import SessionContext from '../../contexts/SessionContext';
import {
    SupplierOrderMessage,
    useGetSupplierOrderComments,
    useAddSupplierOrderComment,
} from '../../hooks/useSupplierOrderComments';

const formatMessageTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

const formatDayDivider = (dateString: string) => {
    const d = new Date(dateString);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        ...(sameYear ? {} : { year: 'numeric' }),
    });
};

const isSameDay = (a: string, b: string) => {
    const da = new Date(a);
    const db = new Date(b);
    return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
};

interface SupplierOrderChatPanelProps {
    supplierOrderId: string;
    supplierName?: string;
}

/**
 * Interactive vendor message thread for a single supplier order — the post-order
 * chat that otherwise lives in SupplierNotesModal's "Messages" tab. Reused by the
 * /chat Communications surface.
 */
const SupplierOrderChatPanel = ({ supplierOrderId, supplierName }: SupplierOrderChatPanelProps) => {
    const sessionContext = useContext(SessionContext);
    const { mutateAsync: fetchComments } = useGetSupplierOrderComments();
    const { mutateAsync: sendComment, isPending: isSending } = useAddSupplierOrderComment();

    const [messages, setMessages] = useState<Array<SupplierOrderMessage>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const [newMessage, setNewMessage] = useState('');
    const endRef = useRef<HTMLDivElement>(null);

    const currentUserName = sessionContext?.isAuthenticated
        ? `${sessionContext.firstName ?? ''} ${sessionContext.lastName ?? ''}`.trim()
        : '';

    const load = useCallback(async (forceRefresh: boolean) => {
        setIsLoading(true);
        setHasFailed(false);
        try {
            const result = await fetchComments({ supplierOrderId, forceRefresh });
            setMessages(result);
        } catch {
            setHasFailed(true);
        } finally {
            setIsLoading(false);
        }
    }, [fetchComments, supplierOrderId]);

    useEffect(() => {
        void load(false);
    }, [load]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length]);

    const handleSend = async () => {
        const trimmed = newMessage.trim();
        if (!trimmed || isSending) return;

        const optimisticId = `optimistic-${Date.now()}`;
        const optimistic: SupplierOrderMessage = {
            _id: optimisticId,
            comment: trimmed,
            fromSupplier: false,
            senderName: null,
            sentByUserId: sessionContext?.isAuthenticated ? sessionContext.userId : undefined,
            sentByUserName: currentUserName || undefined,
            timestamp: new Date().toISOString(),
        };
        setMessages(prev => [...prev, optimistic]);
        setNewMessage('');
        try {
            await sendComment({ supplierOrderId, comment: trimmed });
            await load(true);
        } catch {
            setMessages(prev => prev.filter(m => m._id !== optimisticId));
            setNewMessage(trimmed);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void handleSend();
        }
    };

    return (
        <Box h="100%" display="flex" flexDirection="column" minH={0}>
            <Flex align="center" justify="flex-end" px={3} py={1.5} borderBottomWidth="1px" borderColor="gray.200" bg="white" flexShrink={0}>
                <Tooltip label="Resync messages from vendor" placement="bottom-end" openDelay={200}>
                    <IconButton
                        aria-label="Resync messages"
                        icon={<Icon as={TbRefresh as React.ElementType} w={4} h={4} />}
                        size="sm"
                        variant="ghost"
                        color="gray.500"
                        _hover={{ bg: 'gray.100', color: 'gray.700' }}
                        isLoading={isLoading}
                        onClick={() => void load(true)}
                    />
                </Tooltip>
            </Flex>
            <VStack flex={1} align="stretch" spacing={4} px={5} py={5} overflowY="auto" bg="gray.50" minH={0}>
                {isLoading && messages.length === 0 ? (
                    <Flex py={10} justify="center"><Spinner size="sm" color="gray.500" /></Flex>
                ) : hasFailed ? (
                    <VStack spacing={3} py={10}>
                        <Text fontSize="sm" color="red.500">Failed to load supplier messages.</Text>
                        <Button size="sm" onClick={() => void load(true)}>Retry</Button>
                    </VStack>
                ) : messages.length === 0 ? (
                    <Flex flex={1} py={10} justify="center" align="center">
                        <Text fontSize="sm" color="gray.500">No messages yet. Send one below.</Text>
                    </Flex>
                ) : (
                    messages.map((message, index) => {
                        const isOurs = !message.fromSupplier;
                        const showDayDivider = index === 0 ||
                            !isSameDay(messages[index - 1].timestamp, message.timestamp);
                        // Attribute outbound messages to the actual sender (matches SupplierNotesModal):
                        // only "(you)"/"You" when it was sent by the current user.
                        const sentByCurrentUser = isOurs
                            && !!sessionContext?.isAuthenticated
                            && !!message.sentByUserId
                            && message.sentByUserId === sessionContext.userId;
                        let displayName: string;
                        if (isOurs) {
                            if (message.sentByUserName) {
                                displayName = sentByCurrentUser ? `${message.sentByUserName} (you)` : message.sentByUserName;
                            } else if (message.senderName) {
                                displayName = message.senderName;
                            } else {
                                displayName = sentByCurrentUser ? 'You' : 'Partbay user';
                            }
                        } else {
                            displayName = message.senderName || supplierName || 'Vendor';
                        }
                        const avatarName = message.sentByUserName
                            ?? message.senderName
                            ?? displayName.replace(/\s+\(you\)$/, '');
                        return (
                            <Fragment key={message._id}>
                                {showDayDivider && (
                                    <Flex w="100%" justify="center" py={2}>
                                        <Text
                                            fontSize="11px"
                                            color="gray.500"
                                            fontWeight="medium"
                                            bg="white"
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="full"
                                            px={3}
                                            py={0.5}
                                        >
                                            {formatDayDivider(message.timestamp)}
                                        </Text>
                                    </Flex>
                                )}
                                <HStack
                                    alignSelf={isOurs ? 'flex-end' : 'flex-start'}
                                    align="flex-end"
                                    spacing={2}
                                    maxW="80%"
                                    flexDirection={isOurs ? 'row-reverse' : 'row'}
                                >
                                    <Avatar
                                        size="xs"
                                        name={avatarName}
                                        bg={isOurs ? '#0071E3' : 'orange.400'}
                                        color="white"
                                        flexShrink={0}
                                    />
                                    <VStack align={isOurs ? 'flex-end' : 'flex-start'} spacing={1} minW={0}>
                                        <Box
                                            bg="white"
                                            color="gray.700"
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="2xl"
                                            borderBottomRightRadius={isOurs ? 'md' : '2xl'}
                                            borderBottomLeftRadius={isOurs ? '2xl' : 'md'}
                                            px={4}
                                            py={2.5}
                                            boxShadow="none"
                                        >
                                            <Text
                                                fontSize="11px"
                                                fontWeight="semibold"
                                                color={isOurs ? '#0071E3' : 'orange.500'}
                                                mb={0.5}
                                            >
                                                {displayName}
                                            </Text>
                                            <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.5">{message.comment}</Text>
                                        </Box>
                                        <Text fontSize="10px" color="gray.400" px={1}>{formatMessageTime(message.timestamp)}</Text>
                                    </VStack>
                                </HStack>
                            </Fragment>
                        );
                    })
                )}
                <div ref={endRef} />
            </VStack>

            <Box p={4} borderTopWidth="1px" borderColor="gray.200" bg="white" flexShrink={0}>
                <Box position="relative">
                    <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Send a message..."
                        resize="none"
                        minH="80px"
                        pr="44px"
                        fontSize="sm"
                        borderRadius="xl"
                        borderColor="gray.300"
                        _focus={{ borderColor: '#0071E3', boxShadow: '0 0 0 1px #0071E3' }}
                        isDisabled={isSending}
                    />
                    <IconButton
                        aria-label="Send message"
                        icon={<Icon as={TbArrowUp as React.ElementType} w={4} h={4} />}
                        position="absolute"
                        bottom="8px"
                        right="8px"
                        size="sm"
                        borderRadius="full"
                        bg="#0071E3"
                        color="white"
                        _hover={{ bg: '#3544a0' }}
                        onClick={() => void handleSend()}
                        isLoading={isSending}
                        isDisabled={!newMessage.trim()}
                    />
                </Box>
                <Text fontSize="xs" color="gray.400" mt={1.5} ml={1}>Press Ctrl+Enter to send</Text>
            </Box>
        </Box>
    );
};

export default SupplierOrderChatPanel;
